import React, { useState, useRef, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Plus,
  FileText,
  Clock,
  CheckCircle,
  ChevronRight,
  Sparkles,
  ChevronDown,
  AlertCircle,
  FileEdit,
} from "lucide-react";
import { sopsAPI } from "../api";
import clsx from "clsx";
import { PERMISSIONS } from "../constants/roles";
import HasPermission from "../components/HasPermission";

// Standardized status configuration using standard badge classes and Icons
const statusConfig = {
  draft: { label: "Draft", badgeClass: "badge-neutral", icon: FileEdit },
  pending_review: {
    label: "Pending Review",
    badgeClass: "badge-warning",
    icon: Clock,
  },
  approved: {
    label: "Approved",
    badgeClass: "badge-success",
    icon: CheckCircle,
  },
  deprecated: {
    label: "Deprecated",
    badgeClass: "badge-danger",
    icon: AlertCircle,
  },
};

/**
 * Replacement for SOPCard: Renders a single SOP as a list row.
 */
const SOPListItem = ({ sop, index }) => {
  const status = statusConfig[sop.status] || statusConfig.draft;
  const StatusIcon = status.icon;

  // Function to determine icon color based on status key (used in list row)
  const getStatusIconColor = (key) => {
    switch (key) {
      case "approved":
        return "text-green-500";
      case "pending_review":
        return "text-yellow-500";
      case "deprecated":
        return "text-red-500";
      case "draft":
      default:
        return "text-slate-500";
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Link
        to={`/sops/${sop.id}`}
        // Using a 12-column grid layout for the list row
        // Structure: Title(4), Status(2), Skill(2), Duration(1), Steps(1), AI(1), Action(1)
        className="grid grid-cols-12 items-center p-4 text-sm border-b border-slate-100 transition-colors hover:bg-slate-50 group"
      >
        {/* Column 1: SOP Title & Category (span 4) */}
        <div className="col-span-4 flex flex-col min-w-0 pr-4">
          <span className="font-medium text-slate-800 group-hover:text-blue-600 transition-colors truncate">
            {sop.title}
          </span>
          <span className="text-xs text-slate-500 truncate">
            {sop.category || "General"}
          </span>
        </div>

        {/* Column 2: Status (span 2) */}
        <div className="col-span-2 flex items-center gap-2 text-slate-700">
          {/* Display icon and label */}
          {StatusIcon && (
            <StatusIcon
              className={clsx("w-4 h-4", getStatusIconColor(sop.status))}
            />
          )}
          <span>{status.label}</span>
        </div>

        {/* Column 3: Skill Level (span 2) */}
        <div className="col-span-2 text-slate-700 truncate">
          {sop.skill_level || "N/A"}
        </div>

        {/* Column 4: Duration (span 1) */}
        <div className="col-span-1 text-slate-500">
          {sop.estimated_duration ? `${sop.estimated_duration} min` : "-"}
        </div>

        {/* Column 5: Steps (span 1) */}
        <div className="col-span-1 text-slate-500">
          {Array.isArray(sop.steps) ? sop.steps.length : 0}
        </div>

        {/* Column 6: AI Generated Status (span 1) */}
        {/* <div className="col-span-1 flex justify-center">
          {sop.is_ai_generated ? (
            <Sparkles
              className="w-4 h-4 text-purple-500"
              title="AI Generated"
            />
          ) : (
            <span className="text-xs text-slate-400">-</span>
          )}
        </div> */}

        {/* Column 7: Action Chevron (span 1) */}
        <div className="col-span-1 flex justify-end pr-2">
          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-blue-500 transition-transform group-hover:translate-x-0.5" />
        </div>
      </Link>
    </motion.div>
  );
};

const SOPs = () => {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [isCategoryFilterOpen, setIsCategoryFilterOpen] = useState(false);
  const statusFilterRef = useRef(null);
  const categoryFilterRef = useRef(null);

  const machineId = searchParams.get("machineId");

  const { data: sopsData, isLoading } = useQuery({
    queryKey: [
      "sops",
      { search, status: statusFilter, category: categoryFilter, machineId },
    ],
    queryFn: () =>
      sopsAPI
        .getAll({
          search: search || undefined,
          status: statusFilter || undefined,
          category: categoryFilter || undefined,
          machineId: machineId || undefined,
        })
        .then((res) => res.data),
  });

  const { data: categories } = useQuery({
    queryKey: ["sop-categories"],
    queryFn: () => sopsAPI.getCategories().then((res) => res.data.data),
  });

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        statusFilterRef.current &&
        !statusFilterRef.current.contains(event.target)
      ) {
        setIsStatusFilterOpen(false);
      }
      if (
        categoryFilterRef.current &&
        !categoryFilterRef.current.contains(event.target)
      ) {
        setIsCategoryFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const sops = sopsData?.data || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Standard Operating Procedures
          </h1>
          <p className="text-slate-500">
            Manage and execute SOPs for your machines
          </p>
        </div>
        <HasPermission allowedRoles={PERMISSIONS.MANAGE_MAINTENANCE}>
          <Link to="/sops/create" className="btn btn-primary">
            <Plus className="w-4 h-4" />
            Create SOP
          </Link>
        </HasPermission>
      </div>

      {/* Filters section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative w-full sm:w-96">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search SOPs..."
            className="input w-full h-10 pl-12"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Status Filter Dropdown */}
          <div ref={statusFilterRef} className="relative">
            <button
              onClick={() => setIsStatusFilterOpen((prev) => !prev)}
              className="btn btn-secondary h-10 w-full sm:w-auto justify-between"
            >
              <span className="flex items-center gap-2">
                {statusFilter && statusConfig[statusFilter] ? (
                  <>
                    {/* Display icon in the button */}
                    {React.createElement(statusConfig[statusFilter].icon, {
                      className: clsx(
                        "w-4 h-4",
                        statusFilter === "approved"
                          ? "text-green-500"
                          : statusFilter === "pending_review"
                            ? "text-yellow-500"
                            : statusFilter === "draft"
                              ? "text-slate-500"
                              : "text-red-500",
                      ),
                    })}
                    {statusConfig[statusFilter].label}
                  </>
                ) : (
                  "All Status"
                )}
              </span>
              <ChevronDown
                className={clsx(
                  "w-4 h-4 transition-transform",
                  isStatusFilterOpen && "rotate-180",
                )}
              />
            </button>
            <AnimatePresence>
              {isStatusFilterOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10"
                >
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setStatusFilter("");
                        setIsStatusFilterOpen(false);
                      }}
                      className={clsx(
                        "flex items-center w-full px-4 py-2 text-sm text-left transition-colors",
                        statusFilter === ""
                          ? "bg-blue-50 text-blue-700"
                          : "text-slate-700 hover:bg-slate-50",
                      )}
                    >
                      All Status
                    </button>
                    {Object.entries(statusConfig).map(([key, config]) => {
                      const Icon = config.icon;
                      return (
                        <button
                          key={key}
                          onClick={() => {
                            setStatusFilter(key);
                            setIsStatusFilterOpen(false);
                          }}
                          className={clsx(
                            "flex items-center w-full px-4 py-2 text-sm text-left transition-colors",
                            statusFilter === key
                              ? "bg-blue-50 text-blue-700"
                              : "text-slate-700 hover:bg-slate-50",
                          )}
                        >
                          <Icon
                            className={clsx(
                              "w-4 h-4 mr-3",
                              key === "approved"
                                ? "text-green-500"
                                : key === "pending_review"
                                  ? "text-yellow-500"
                                  : key === "draft"
                                    ? "text-slate-500"
                                    : "text-red-500",
                            )}
                          />
                          {config.label}
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {/* Category Filter Dropdown */}
          <div ref={categoryFilterRef} className="relative">
            <button
              onClick={() => setIsCategoryFilterOpen((prev) => !prev)}
              className="btn btn-secondary h-10 w-full sm:w-auto justify-between"
            >
              <span>{categoryFilter || "All Categories"}</span>
              <ChevronDown
                className={clsx(
                  "w-4 h-4 transition-transform",
                  isCategoryFilterOpen && "rotate-180",
                )}
              />
            </button>
            <AnimatePresence>
              {isCategoryFilterOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10"
                >
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setCategoryFilter("");
                        setIsCategoryFilterOpen(false);
                      }}
                      className={clsx(
                        "flex items-center w-full px-4 py-2 text-sm text-left transition-colors",
                        categoryFilter === ""
                          ? "bg-blue-50 text-blue-700"
                          : "text-slate-700 hover:bg-slate-50",
                      )}
                    >
                      All Categories
                    </button>
                    {categories?.map((cat) => (
                      <button
                        key={cat}
                        onClick={() => {
                          setCategoryFilter(cat);
                          setIsCategoryFilterOpen(false);
                        }}
                        className={clsx(
                          "flex items-center w-full px-4 py-2 text-sm text-left transition-colors",
                          categoryFilter === cat
                            ? "bg-blue-50 text-blue-700"
                            : "text-slate-700 hover:bg-slate-50",
                        )}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* SOPs List View */}
      {isLoading ? (
        <div className="card overflow-hidden p-0">
          {/* Skeletons mimicking the 12-column layout: 4-2-2-1-1-1-1 */}
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="grid grid-cols-12 items-center p-4 border-b border-slate-200 last:border-b-0"
            >
              {/* Title/Category (4) */}
              <div className="col-span-4">
                <div className="h-5 w-48 skeleton mb-1" />
                <div className="h-3 w-24 skeleton" />
              </div>
              {/* Status (2) */}
              <div className="col-span-2">
                <div className="h-4 w-20 skeleton" />
              </div>
              {/* Skill Level (2) */}
              <div className="col-span-2">
                <div className="h-4 w-28 skeleton" />
              </div>
              {/* Duration (1) */}
              <div className="col-span-1">
                <div className="h-4 w-12 skeleton" />
              </div>
              {/* Steps (1) */}
              <div className="col-span-1">
                <div className="h-4 w-8 skeleton" />
              </div>
              {/* AI Gen (1) */}
              <div className="col-span-1" />
              {/* Action (1) */}
              <div className="col-span-1" />
            </div>
          ))}
        </div>
      ) : sops.length > 0 ? (
        <div className="card overflow-hidden p-0">
          {/* Table Header (4-2-2-1-1-1-1) */}
          <div className="grid grid-cols-12 items-center text-xs font-semibold uppercase text-slate-500 bg-slate-50 border-b border-slate-200 px-4 py-3">
            <div className="col-span-4">SOP / Category</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Skill Level</div>
            <div className="col-span-1">Duration</div>
            <div className="col-span-1">Steps</div>
            {/* <div className="col-span-1 text-center">AI</div> */}
            <div className="col-span-1"></div> {/* Action column */}
          </div>

          {/* List Body */}
          <div className="divide-y divide-slate-100">
            <AnimatePresence>
              {sops.map((sop, index) => (
                <SOPListItem key={sop.id} sop={sop} index={index} />
              ))}
            </AnimatePresence>
          </div>
        </div>
      ) : (
        <div className="card p-12 text-center">
          <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-800 mb-2">
            No SOPs found
          </h3>
          <p className="text-slate-500 mb-4">
            {search || statusFilter || categoryFilter
              ? "Try adjusting your filters"
              : "Get started by creating your first SOP"}
          </p>
          <HasPermission allowedRoles={PERMISSIONS.MANAGE_MAINTENANCE}>
            <Link to="/sops/create" className="btn btn-primary inline-flex">
              <Plus className="w-4 h-4" />
              Create SOP
            </Link>
          </HasPermission>
        </div>
      )}

      {/* Pagination */}
      {sopsData?.pagination && sopsData.pagination.pages > 1 && (
        <div className="flex justify-center gap-2">
          {[...Array(sopsData.pagination.pages)].map((_, i) => (
            <button
              key={i}
              // Pagination logic remains simple for display purposes
              className={clsx(
                "w-10 h-10 rounded-xl transition-colors",
                i + 1 === sopsData.pagination.page
                  ? "btn-primary"
                  : "bg-white border border-slate-300 text-slate-500 hover:bg-slate-100",
              )}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SOPs;
